<script setup lang="ts">

import { faCheck } from '@fortawesome/pro-regular-svg-icons'

import { AppText } from '@shared/ui/typography'
import { AppIcon } from '@shared/ui/icons'
import { AppDatePicker, InputPrimary, NativeSelect } from '@shared/ui/fields'
import { BtnRadio, BtnSecondary } from '@shared/ui/buttons'

import LessonBalanceAvailable from './LessonBalanceAvailable.vue'
import { useLessonRecurringSchedule } from '../../model/lessons/lesson-recurring-schedule.hook'
import { LessonRecurringFinishType } from '../../model/lessons/enums'

defineOptions({
  name: 'LessonRecurringSchedule',
})

const {
  refStartDate,
  refMinDate,
  refDayTabs,
  refSelectedDayName,
  refStartOptions,
  refDurationOptions,
  refWeekCycleOptions,
  refSelectedDay,
  refBalance,
  refFinishType,
  refOccurrences,
  refLessons,
  refFinishDate,
  refIsSubmitting,
  setStartDate,
  selectDay,
  setStartTime,
  setDuration,
  setWeekCycle,
  setFinishType,
  setOccurrences,
  setLessons,
  setFinishDate,
} = useLessonRecurringSchedule()
</script>

<template>
  <div class="lesson-recurring-schedule">
    <AppText name="body1-medium">
      Lesson details
    </AppText>
    <AppDatePicker
      class="lesson-recurring-schedule__date"
      label="Start date"
      :value="refStartDate"
      :min-date="refMinDate"
      :disabled="refIsSubmitting"
      @change="setStartDate"
    />
    <div class="lesson-recurring-schedule__group">
      <AppText
        name="body2"
        color="slate-900"
      >
        Repeat on
      </AppText>
      <div class="lesson-recurring-schedule__days">
        <BtnSecondary
          v-for="tab in refDayTabs"
          :key="tab.day"
          :text="tab.day"
          :selected="tab.selected"
          @click="selectDay(tab.day)"
        >
          <template
            v-if="tab.checked"
            #iconRight
          >
            <AppIcon
              :icon="faCheck"
              size="small"
              color="emerald-600"
            />
          </template>
        </BtnSecondary>
      </div>
    </div>
    <div
      v-if="refSelectedDay"
      class="lesson-recurring-schedule__day"
    >
      <AppText
        name="body2-medium"
        color="slate-900"
      >
        {{ refSelectedDayName }}
      </AppText>
      <div class="lesson-recurring-schedule__time">
        <NativeSelect
          class="lesson-recurring-schedule__slot"
          name="recurring_time"
          label="Time"
          :options="refStartOptions"
          :value="refSelectedDay.startTime"
          :disabled="refIsSubmitting"
          @change="setStartTime"
        />
        <AppText
          class="lesson-recurring-schedule__separator"
          name="body2-medium"
          color="slate-900"
        >
          to
        </AppText>
        <NativeSelect
          class="lesson-recurring-schedule__slot"
          name="recurring_duration"
          :options="refDurationOptions"
          :value="refSelectedDay.duration"
          :disabled="refIsSubmitting"
          @change="setDuration"
        />
      </div>
      <div class="lesson-recurring-schedule__cycle">
        <NativeSelect
          class="lesson-recurring-schedule__cycle-select"
          name="repeat_every"
          label="Repeat every"
          :options="refWeekCycleOptions"
          :value="refSelectedDay.weekCycle"
          :disabled="refIsSubmitting"
          @change="setWeekCycle"
        />
        <AppText
          class="lesson-recurring-schedule__separator"
          name="body2"
          color="slate-900"
        >
          week
        </AppText>
      </div>
    </div>
    <LessonBalanceAvailable
      v-if="refBalance"
      :value="refBalance"
    />
    <div class="lesson-recurring-schedule__group">
      <AppText
        name="body2"
        color="slate-900"
      >
        Ends:
      </AppText>
      <div class="lesson-recurring-schedule__end">
        <BtnRadio
          text="After"
          :checked="refFinishType === LessonRecurringFinishType.Occurrences"
          @click="setFinishType(LessonRecurringFinishType.Occurrences)"
        />
        <InputPrimary
          id="occurrences"
          class="lesson-recurring-schedule__count"
          type="number"
          :min="0"
          :max="200"
          :value="refOccurrences"
          :disabled="refFinishType !== LessonRecurringFinishType.Occurrences || refIsSubmitting"
          @input="setOccurrences"
        />
        <AppText
          name="body2"
          color="slate-900"
        >
          occurrences
        </AppText>
      </div>
      <div class="lesson-recurring-schedule__end">
        <BtnRadio
          text="After"
          :checked="refFinishType === LessonRecurringFinishType.Lessons"
          @click="setFinishType(LessonRecurringFinishType.Lessons)"
        />
        <InputPrimary
          id="lessons"
          class="lesson-recurring-schedule__count"
          type="number"
          :min="0"
          :max="200"
          :value="refLessons"
          :disabled="refFinishType !== LessonRecurringFinishType.Lessons || refIsSubmitting"
          @input="setLessons"
        />
        <AppText
          name="body2"
          color="slate-900"
        >
          lessons
        </AppText>
      </div>
      <div class="lesson-recurring-schedule__end">
        <BtnRadio
          text="On"
          :checked="refFinishType === LessonRecurringFinishType.Date"
          @click="setFinishType(LessonRecurringFinishType.Date)"
        />
        <AppDatePicker
          class="lesson-recurring-schedule__finish"
          :value="refFinishDate"
          :min-date="refStartDate"
          :disabled="refFinishType !== LessonRecurringFinishType.Date || refIsSubmitting"
          @change="setFinishDate"
        />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss" src="./LessonRecurringSchedule.scss" />
